import axios from 'axios';
import { useEffect, useState } from 'react';
import QRCode from 'react-qr-code';
import { toast } from 'react-toastify';
import { BASE_URL } from '../../constants/api-urls';
import { MfaEnrollmentModalStyle } from './MfaEnrollmentModal.style';
import { FilledButtonStyle } from '../../styles/common.style';
import { getAuth0AuthHeaders } from '../../utils/auth0Session';

type MfaEnrollmentModalProps = {
  open: boolean;
  userId: string;
  email?: string;
  onClose: () => void;
  onSuccess: () => void;
};

const getErrorMessage = (err: unknown, fallback: string) => {
  if (axios.isAxiosError(err)) {
    return err.response?.data?.message || fallback;
  }
  if (err instanceof Error) {
    return err.message;
  }
  return fallback;
};

export const MfaEnrollmentModal = ({
  open,
  userId,
  email,
  onClose,
  onSuccess,
}: MfaEnrollmentModalProps) => {
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [enrollmentToken, setEnrollmentToken] = useState<string | null>(null);
  const [otp, setOtp] = useState('');
  const [error, setError] = useState<string | null>(null);

  const loadEnrollment = async () => {
    if (!userId) {
      return;
    }

    const headers = getAuth0AuthHeaders();
    if (!headers.Authorization) {
      setError('Your session has expired. Please log out and log in again.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setOtp('');
    setQrCode(null);
    setEnrollmentToken(null);

    try {
      const response = await axios.post(
        `${BASE_URL}/auth0/mfa/enroll/start`,
        { userId, email },
        { headers }
      );

      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Failed to start MFA enrollment');
      }

      const barcodeUri = response.data?.data?.barcode_uri;
      const nextEnrollmentToken = response.data?.data?.enrollmentToken;

      if (!barcodeUri || !nextEnrollmentToken) {
        throw new Error('Failed to start MFA enrollment');
      }

      setQrCode(barcodeUri);
      setEnrollmentToken(nextEnrollmentToken);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to start MFA enrollment'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    loadEnrollment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userId, email]);

  const handleConfirm = async () => {
    const headers = getAuth0AuthHeaders();

    if (!enrollmentToken) {
      setError('Enrollment session expired. Please close and try again.');
      return;
    }

    if (!headers.Authorization) {
      setError('Your session has expired. Please log out and log in again.');
      return;
    }

    if (!otp.trim()) {
      setError('Enter the 6-digit code from your authenticator app.');
      return;
    }

    setConfirming(true);
    setError(null);

    try {
      const response = await axios.post(
        `${BASE_URL}/auth0/mfa/enroll/confirm`,
        {
          userId,
          otp: otp.trim(),
          enrollmentToken,
        },
        { headers }
      );

      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Failed to enable MFA');
      }

      toast.success('MFA enabled successfully');
      onSuccess();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to enable MFA'));
    } finally {
      setConfirming(false);
    }
  };

  const confirmDisabled = loading || confirming || !qrCode || !enrollmentToken;

  return (
    <MfaEnrollmentModalStyle
      open={open}
      footer={null}
      onCancel={onClose}
      destroyOnClose
      width={480}
      maskClosable={false}
      centered
    >
      <h1 className="heading">Set up MFA</h1>

      <p className="body-text">
        Scan this QR code with Google Authenticator, Authy, or a similar app, then enter the
        6-digit code below.
      </p>

      {loading ? (
        <p className="body-text">Preparing your QR code…</p>
      ) : qrCode ? (
        <>
          <div className="qrCodeDiv">
            <div className="qrCode">
              <div className="innerQrCode">
                <QRCode size={180} value={qrCode} viewBox="0 0 256 256" />
              </div>
            </div>
          </div>

          <div className="thenSeperator">
            <div className="sepLine" />
            <p>Then</p>
            <div className="sepLine" />
          </div>
        </>
      ) : null}

      <div className="main-form-login">
        <label htmlFor="mfa-enrollment-otp">One-time password</label>
        <input
          id="mfa-enrollment-otp"
          placeholder="123456"
          value={otp}
          maxLength={6}
          inputMode="numeric"
          autoComplete="one-time-code"
          disabled={loading || confirming || !qrCode}
          onChange={event => setOtp(event.target.value.replace(/\D/g, ''))}
        />
      </div>

      {error ? (
        <p className="signup-error" style={{ color: 'red' }}>
          {error}
        </p>
      ) : null}

      <div className="footer-actions">
        {error && !loading ? (
          <FilledButtonStyle
            type="button"
            className="btn-cancel"
            onClick={loadEnrollment}
            background="transparent"
            width="96px"
            disabled={confirming}
          >
            Retry
          </FilledButtonStyle>
        ) : null}
        <FilledButtonStyle
          type="button"
          className="btn-cancel"
          onClick={onClose}
          background="transparent"
          width="96px"
          disabled={confirming}
        >
          Cancel
        </FilledButtonStyle>
        <FilledButtonStyle
          type="button"
          className="btn-confirm"
          onClick={handleConfirm}
          width="120px"
          disabled={confirmDisabled}
        >
          {confirming ? 'Enabling…' : 'Enable MFA'}
        </FilledButtonStyle>
      </div>
    </MfaEnrollmentModalStyle>
  );
};

export default MfaEnrollmentModal;
